/**
 * @fileoverview Leitura dos campos de hora do evento e comparação dos
 * instantes que eles produzem.
 *
 * O caminho que estas funções cobrem, do campo até o valor gravado:
 *
 *   1. O usuário digita no campo de início/fim de procedimento ou anestesia.
 *      A máscara `##:##` aceita QUALQUER par de dígitos — `45:18` inclusive.
 *   2. `EventEdit` lê esse texto em `calculateHourEmergency` e em
 *      `processDatesFromHourFields` e aplica a hora sobre a data do
 *      procedimento, com `moment.set('hour', ...)`.
 *   3. A data resultante vira `Timestamp` do Firestore e é gravada.
 *
 * Dois defeitos moravam nesse caminho, e cada função aqui fecha um deles:
 *
 *   - `moment.set('hour', 45)` não recusa nada: interpreta como "dia seguinte
 *     às 21h". Dentro de um ciclo de reatividade, cada volta somava um dia até
 *     a data passar do ano 9999 e o Firestore recusar o Timestamp.
 *     `parseHourMinute` corta isso na entrada.
 *   - A guarda que decidia se valia a pena reescrever a data comparava um
 *     moment com um Timestamp — comparação sempre falsa. A reescrita
 *     acontecia toda vez, acordava o watcher que a chamava de volta, e o ciclo
 *     não fechava. `isSameInstant` compara instantes de verdade.
 */

/** Limites do Timestamp do Firestore: 0001-01-01T00:00:00Z a 9999-12-31T23:59:59Z. */
export const FIRESTORE_MIN_SECONDS = -62135596800;
export const FIRESTORE_MAX_SECONDS = 253402300799;

/** Maior hora que existe num relógio de 24 horas. */
export const MAX_HOUR = 23;
/** Maior minuto que existe numa hora. */
export const MAX_MINUTE = 59;

/**
 * Lê um texto `HH:mm` e devolve a hora e o minuto apenas quando ambos são
 * válidos. Fora da faixa devolve null, porque `moment.set('hour', 45)` não
 * recusa nada: ele empurra a data para o dia seguinte.
 *
 * @param {string} text - Conteúdo do campo, no formato `HH:mm`.
 * @returns {{hour: number, minute: number}|null} A hora lida, ou null.
 */
export function parseHourMinute(text) {
  if (typeof text !== 'string') {
    return null;
  }
  const found = text.match(/^\s*(\d{1,2}):(\d{1,2})\s*$/);
  if (!found) {
    return null;
  }
  const hour = parseInt(found[1], 10);
  const minute = parseInt(found[2], 10);
  if (hour < 0 || hour > MAX_HOUR || minute < 0 || minute > MAX_MINUTE) {
    return null;
  }
  return { hour, minute };
}

/**
 * Lê hora e minuto do texto do campo aceitando as formas parciais que a máscara
 * produz durante a digitação — `08:` vale 08:00, como sempre valeu.
 *
 * Existe separada de `parseHourMinute` porque as duas respondem a perguntas
 * diferentes: esta pergunta *"dá para aproveitar o que está digitado?"* e serve
 * ao processamento das datas; a estrita pergunta *"isto é uma hora completa?"* e
 * serve à validação que o usuário vê. Só o que ambas recusam é a hora fora do
 * relógio — a que faz `moment.set('hour', ...)` empurrar a data para o dia
 * seguinte.
 *
 * @param {string} text - Conteúdo do campo.
 * @returns {{hour: number, minute: number}|null} A hora lida, ou null quando
 *   nada aproveitável foi digitado ou a hora está fora do relógio.
 */
export function readHourMinute(text) {
  if (typeof text !== 'string') {
    return null;
  }
  const found = text.match(/(.*):(.*)/);
  if (!found) {
    return null;
  }
  const hour = parseInt(found[1], 10);
  const minute = parseInt(found[2] || 0, 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return null;
  }
  if (hour < 0 || hour > MAX_HOUR || minute < 0 || minute > MAX_MINUTE) {
    return null;
  }
  return { hour, minute };
}

/**
 * Lê hora e minuto de um campo que tanto pode ser o texto `HH:mm` quanto um
 * valor com instante próprio (Timestamp, Date ou moment).
 *
 * MUDANÇA DE COMPORTAMENTO — o ramo do Timestamp lia errado. O código anterior
 * fazia `moment(timestamp).format('HH')`, e `moment` de um Timestamp do
 * Firestore é inválido: o format devolvia a string `'Invalid date'` e o
 * `set('hour', ...)` seguinte não mudava nada. Na prática a hora de início era
 * IGNORADA nesse ramo, e o cálculo de emergência acabava decidindo pela hora
 * da data do procedimento. Aqui a hora é lida pelo instante, via `toDate()`.
 *
 * Consequência: quando a hora de início e a hora da data do procedimento
 * divergem, o flag de emergência passa a refletir a hora de início — e
 * emergência altera o valor cobrado. Quando os dois horários coincidem, que é
 * o caso comum, nada muda.
 *
 * @param {*} value - Texto `HH:mm`, ou valor com instante.
 * @returns {{hour: number, minute: number}|null} A hora lida, ou null.
 */
export function resolveHourMinute(value) {
  const fromText = parseHourMinute(value);
  if (fromText) {
    return fromText;
  }
  if (value && typeof value.toDate === 'function') {
    const date = value.toDate();
    if (!Number.isNaN(date.getTime())) {
      return { hour: date.getHours(), minute: date.getMinutes() };
    }
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return { hour: value.getHours(), minute: value.getMinutes() };
  }
  return null;
}

/**
 * Converte para milissegundos qualquer forma que o caminho das datas produz:
 * moment, Date, Timestamp do Firestore, ou o Timestamp que perdeu o protótipo
 * na serialização e chega como objeto simples.
 *
 * `Timestamp.valueOf()` do Firestore devolve string, não número — por isso é o
 * `toDate()` que vale para ele, e a leitura por `valueOf()` fica para moment e
 * Date.
 *
 * @param {*} value - moment, Date, Timestamp ou objeto com seconds.
 * @returns {number|null} Milissegundos, ou null quando não há instante legível.
 */
export function toMillis(value) {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value.toDate === 'function') {
    const millis = value.toDate().getTime();
    if (!Number.isNaN(millis)) {
      return millis;
    }
  }
  if (typeof value.valueOf === 'function') {
    const millis = value.valueOf();
    if (typeof millis === 'number' && !Number.isNaN(millis)) {
      return millis;
    }
  }
  const seconds = value.seconds ?? value._seconds;
  const nanoseconds = value.nanoseconds ?? value._nanoseconds ?? 0;
  if (typeof seconds === 'number' && !Number.isNaN(seconds)) {
    return (seconds * 1000) + Math.floor(nanoseconds / 1e6);
  }
  return null;
}

/**
 * Diz se o instante cabe na faixa que o Timestamp do Firestore aceita. Fora
 * dela o construtor lança, e a exceção no meio de um emit trava o campo.
 *
 * @param {*} value - moment, Date, Timestamp ou objeto com seconds.
 * @returns {boolean} true quando o valor pode virar Timestamp com segurança.
 */
export function isInFirestoreRange(value) {
  const millis = toMillis(value);
  if (millis === null) {
    return false;
  }
  const seconds = Math.floor(millis / 1000);
  return seconds >= FIRESTORE_MIN_SECONDS && seconds <= FIRESTORE_MAX_SECONDS;
}

/**
 * Diz se dois valores apontam para o mesmo instante, mesmo vindo em formas
 * diferentes. Comparar um moment direto com um Timestamp devolve sempre false,
 * e uma guarda escrita assim nunca segura a reescrita que a chama de volta.
 *
 * @param {*} left - moment, Date ou Timestamp.
 * @param {*} right - moment, Date ou Timestamp.
 * @returns {boolean} true quando ambos apontam para o mesmo instante.
 */
export function isSameInstant(left, right) {
  const leftMillis = toMillis(left);
  const rightMillis = toMillis(right);
  if (leftMillis === null || rightMillis === null) {
    return false;
  }
  return leftMillis === rightMillis;
}

/**
 * Diz se o instante cai no plantão de emergência: fim de semana, depois das
 * 18h ou antes das 7h.
 *
 * Limites, para não restarem dúvidas na leitura: 18h em ponto ainda NÃO é
 * emergência e 19h já é; 6h59 é e 7h em ponto não é. A regra vivia inline em
 * `calculateHourEmergency` e não tinha teste; foi trazida para cá sem alterar
 * uma linha da condição, para poder ser coberta.
 *
 * @param {object} dateAsMoment - Instante do procedimento, como moment.
 * @returns {boolean} true quando o horário é de emergência.
 */
export function isEmergencyMoment(dateAsMoment) {
  return dateAsMoment.weekday() === 0
    || dateAsMoment.weekday() === 6
    || dateAsMoment.hours() > 18
    || dateAsMoment.hours() < 7;
}

/**
 * Resolve o valor que o cálculo de emergência usa como hora de início.
 *
 * A preferência pelo campo de data sobre o campo de hora é o que torna a ORDEM
 * das chamadas decisiva: enquanto as datas não forem sincronizadas, o valor
 * gravado antes vence a hora que o usuário acabou de digitar.
 *
 * @param {object} form - Form do evento.
 * @param {object} companyFlags - Flags da empresa em uso.
 * @returns {*} Timestamp gravado ou o texto `HH:mm` do campo de hora.
 */
export function resolveBaseHourValue(form, companyFlags) {
  if (companyFlags?.valuePerHour && companyFlags?.baseValueHour === 'procedureInterval') {
    return form?.procedureStart || form?._procedureStart;
  }
  return form?.anesthesiaStart || form?._anesthesiaStart;
}

/**
 * Converte para Timestamp o que carrega um instante aceito pelo Firestore, e
 * devolve null para o resto. Normalizar em vez de descartar é o que preserva o
 * valor que o sync com o additionalData traz como Date.
 *
 * @param {*} value - Timestamp, Date, moment ou objeto com seconds.
 * @param {Function} Timestamp - O construtor de Timestamp do Firestore.
 * @returns {*} O valor como Timestamp, ou null.
 */
export function asTimestampOrNull(value, Timestamp) {
  if (value instanceof Timestamp) {
    return value;
  }
  const millis = toMillis(value);
  if (millis === null) {
    return null;
  }
  const date = new Date(millis);
  if (!isInFirestoreRange(date)) {
    return null;
  }
  return Timestamp.fromDate(date);
}

/**
 * Transforma os campos de hora `HH:mm` do evento nos campos de data
 * correspondentes, sobre a data do procedimento.
 *
 * Vive aqui, e não no componente, para que a regra possa ser exercitada como
 * ela roda — o EventEdit apenas delega.
 *
 * Os quatro campos seguem a mesma forma: só entram quando há texto E hora
 * legível, e gravam Timestamp. Entrar sem hora legível fabricava um valor igual
 * à data do procedimento, que o usuário nunca digitou; gravar moment fazia a
 * normalização final descartá-lo.
 *
 * @param {object} params - Dependências do componente.
 * @param {object} params.form - Form do evento, mutado no lugar.
 * @param {boolean} params.onlyHourOnEventBeginEnd - Flag do ramo de hora.
 * @param {Function} params.Timestamp - Construtor de Timestamp do Firestore.
 * @param {Function} params.validateAllTimes - Validação de intervalos do evento.
 * @returns {boolean} false quando a validação de intervalos reprova.
 */
export function resolveEventHourFields({ form, onlyHourOnEventBeginEnd, Timestamp, validateAllTimes }) {
  if (!onlyHourOnEventBeginEnd) {
    return true;
  }

  const dtProcedure = moment(form.dtProcedure?.toDate());
  const procedureStartHour = readHourMinute(form._procedureStart);
  const anesthesiaStartHour = readHourMinute(form._anesthesiaStart);

  if (procedureStartHour) {
    dtProcedure.set('hour', procedureStartHour.hour);
    dtProcedure.set('minute', procedureStartHour.minute);
    form.dtProcedure = Timestamp.fromDate(dtProcedure.toDate());
  } else if (anesthesiaStartHour) {
    dtProcedure.set('hour', anesthesiaStartHour.hour);
    dtProcedure.set('minute', anesthesiaStartHour.minute);
    form.dtProcedure = Timestamp.fromDate(dtProcedure.toDate());
  }

  const campos = [
    { texto: form._anesthesiaStart, hora: anesthesiaStartHour, destino: 'anesthesiaStart' },
    { texto: form._anesthesiaEnd, hora: readHourMinute(form._anesthesiaEnd), destino: 'anesthesiaEnd' },
    { texto: form._procedureStart, hora: procedureStartHour, destino: 'procedureStart' },
    { texto: form._procedureEnd, hora: readHourMinute(form._procedureEnd), destino: 'procedureEnd' },
  ];
  for (const campo of campos) {
    if (!campo.texto || !campo.hora) {
      continue;
    }
    try {
      const instante = moment(dtProcedure);
      instante.set('hour', campo.hora.hour);
      instante.set('minute', campo.hora.minute);
      form[campo.destino] = Timestamp.fromDate(instante.toDate());
    } catch (error) {
      console.error(`Erro ao converter _${campo.destino} para timestamp`, error);
    }
  }

  if (!validateAllTimes(form)) {
    return false;
  }

  for (const campo of campos) {
    form[campo.destino] = asTimestampOrNull(form[campo.destino], Timestamp);
  }
  return true;
}

/**
 * Diz se o save deve recalcular a emergência antes de gravar.
 *
 * Só o ramo de hora precisa: nele a hora digitada vira data dentro do próprio
 * save, e sem recalcular depois o evento é gravado com a classificação da hora
 * anterior. No ramo padrão a data já foi recalculada na tela, e recalcular aqui
 * apagaria a emergência que o usuário marcou à mão no switch.
 *
 * Vive como função para que a decisão tenha teste — dentro do componente ela
 * não tem como ser exercitada.
 *
 * @param {boolean} onlyHourOnEventBeginEnd - Flag do ramo de hora da empresa.
 * @returns {boolean} true quando o save deve recalcular.
 */
export function shouldRecalcEmergencyOnSave(onlyHourOnEventBeginEnd) {
  return !!onlyHourOnEventBeginEnd;
}

/**
 * Diz se o cálculo automático de emergência pode escrever no evento.
 *
 * Só evento NOVO é classificado automaticamente. Em evento já gravado a flag é
 * do usuário: ele a decide no switch, e abrir um evento cuja data cai em horário
 * de plantão religava a flag por cima da escolha dele — emergência altera o
 * valor cobrado.
 *
 * @param {object} params - Estado do componente.
 * @param {boolean} params.isNew - true quando o evento ainda não foi gravado.
 * @param {boolean} [params.notCalculateAutomaticEmergency] - Flag da empresa que
 *   desliga o cálculo automático por completo.
 * @returns {boolean} true quando o cálculo pode escrever.
 */
export function canCalculateEmergency({ isNew, notCalculateAutomaticEmergency } = {}) {
  if (notCalculateAutomaticEmergency) {
    return false;
  }
  return !!isNew;
}
